import { cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import type { PersonaOpportunityDrivenBy } from "../../../lib/persona-opportunity-row-model.js";
import { ExpandableCellText } from "../../table/PersonaOpportunityTable/persona-opportunity-cells.js";

import type { HubChromeOpportunityRow } from "./hub-chrome.types.js";

interface AngleDriver {
  kind: PersonaOpportunityDrivenBy;
  label: string;
  text: string;
}

/** Angle with its pain point and motivation underneath; the driving one leads and is tinted. */
export function OpportunityHubAngleCell({ row }: { row: HubChromeOpportunityRow }) {
  const { t } = useTranslation();
  const drivenLabel = t("personaOpportunities.active.drivenByBadge", "Driving");
  const painPoint: AngleDriver = {
    kind: "painpoint",
    label: t("personaOpportunities.hub.opportunities.driver.painPoint", "Pain"),
    text: row.painPoint?.trim() ?? "",
  };
  const motivation: AngleDriver = {
    kind: "motivation",
    label: t("personaOpportunities.hub.opportunities.driver.motivation", "Motive"),
    text: row.motivation?.trim() ?? "",
  };
  const ordered = row.drivenBy === "motivation" ? [motivation, painPoint] : [painPoint, motivation];
  const drivers = ordered.filter((driver) => driver.text !== "");

  return (
    <div className="space-y-1.5">
      <ExpandableCellText
        text={row.angle}
        className="line-clamp-2 whitespace-normal break-words font-semibold leading-snug text-foreground"
      />
      {drivers.length > 0 ? (
        <dl className="space-y-0.5">
          {drivers.map((driver) => {
            const isDriving = row.drivenBy === driver.kind;
            return (
              <div key={driver.kind} className="flex items-start gap-1.5">
                <dt
                  className={cn(
                    "shrink-0 rounded-sm px-1 py-px text-[10px] font-semibold uppercase tracking-wide",
                    isDriving ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground",
                  )}
                >
                  {driver.label}
                  {isDriving ? <span className="sr-only"> {drivenLabel}</span> : null}
                </dt>
                <dd
                  className={cn(
                    "line-clamp-2 whitespace-normal break-words text-xs leading-snug",
                    isDriving ? "font-medium text-foreground" : "text-muted-foreground",
                  )}
                >
                  {driver.text}
                </dd>
              </div>
            );
          })}
        </dl>
      ) : null}
    </div>
  );
}
